import React, { useRef } from "react";
import Select from "react-select";
import { DUMMY_LEDGERS } from "../data/dummyVoucherData";
import { getFilteredLedgers } from "../data/ledgerFilterRules";

const getSelectStyles = (isWarn) => ({
  container: (base) => ({
    ...base,
    flex: 1,
    width: "100%",
  }),
  control: (base, state) => ({
    ...base,
    minHeight: "25px",
    height: "25px",
    fontSize: "11px",
    borderColor: isWarn ? "#f59e0b" : state.isFocused ? "#064E48" : "#cbd5e0",
    backgroundColor: isWarn ? "#fffbeb" : "#ffffff",
    boxShadow: state.isFocused ? "0 0 0 1px #064E48" : "none",
    borderRadius: "3px",
    padding: "0",
    cursor: "text",
    "&:hover": {
      borderColor: isWarn ? "#f59e0b" : "#064E48",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: "25px",
    padding: "0 6px",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({
    ...base,
    margin: "0",
    padding: "0",
    fontSize: "11px",
    color: "#1a202c",
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: "11px",
    color: "#94a3b8",
    margin: "0",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "11px",
    color: "#1a202c",
    margin: "0",
    fontWeight: 500,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "25px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "1px 4px",
    color: "#64748b",
    svg: {
      width: "14px",
      height: "14px",
    },
    "&:hover": {
      color: "#064E48",
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "1px 2px",
    color: "#94a3b8",
    svg: {
      width: "12px",
      height: "12px",
    },
    "&:hover": {
      color: "#e53e3e",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999,
  }),
  menu: (base) => ({
    ...base,
    fontSize: "11px",
    zIndex: 99999,
    marginTop: "2px",
    borderRadius: "4px",
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.16)",
    border: "1px solid #cbd5e0",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "220px",
    padding: "2px 0",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "11px",
    padding: "5px 8px",
    backgroundColor: state.isSelected
      ? "#064E48"
      : state.isFocused
      ? "#e6fffa"
      : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#1a202c",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#064E48",
      color: "#ffffff",
    },
  }),
});

const filterOption = (candidate, input) => {
  if (!input) return true;
  const q = input.toLowerCase().trim();
  const label = String(candidate.label || "").toLowerCase();
  const group = String(candidate.data?.group || "").toLowerCase();
  return label.includes(q) || group.includes(q);
};

const formatOptionLabel = (option, { context }) => {
  if (context === "value") {
    return option.label;
  }
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
      <span style={{ fontWeight: 500 }}>{option.label}</span>
      {option.group && (
        <span
          style={{
            fontSize: "9.5px",
            color: "#4a5568",
            backgroundColor: "#edf2f7",
            padding: "1px 6px",
            borderRadius: "3px",
            whiteSpace: "nowrap",
          }}
        >
          {option.group}
        </span>
      )}
    </div>
  );
};

export default function VoucherTable({
  rows,
  ledgers = [],
  voucherType,
  isModifyMode = false,
  onRowChange,
  onAmountComplete,
  onDeleteRow,
  onAddRow,
  onOpenLedgerSearch,
}) {
  const allLedgers = ledgers && ledgers.length > 0 ? ledgers : DUMMY_LEDGERS;

  const ledgerSelectRefs = useRef([]);
  const drInputRefs = useRef([]);
  const crInputRefs = useRef([]);

  const handleAmountChange = (index, field, value) => {
    // Clean numeric input: allow digits, one decimal point, or empty
    const sanitized = value.replace(/[^0-9.]/g, "");
    // Prevent multiple decimals
    const parts = sanitized.split(".");
    const finalVal = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : sanitized;

    onRowChange(index, field, finalVal);
  };

  const handleTypeChange = (index, newType) => {
    onRowChange(index, "type", newType);
  };

  const handleLedgerSelectChange = (index, selectedOption, isDr) => {
    if (selectedOption) {
      onRowChange(index, "ledger", selectedOption.value, selectedOption.headCode);
      // Automatically advance focus to active amount input
      setTimeout(() => {
        if (isDr) {
          drInputRefs.current[index]?.focus();
          drInputRefs.current[index]?.select?.();
        } else {
          crInputRefs.current[index]?.focus();
          crInputRefs.current[index]?.select?.();
        }
      }, 40);
    } else {
      onRowChange(index, "ledger", "", null);
    }
  };

  // Keyboard navigation: Enter on ledger select -> focus active amount field
  const handleLedgerKeyDown = (e, index, isDr) => {
    if (e.key === "Enter" && rows[index]?.ledger) {
      e.preventDefault();
      if (isDr) {
        drInputRefs.current[index]?.focus();
        drInputRefs.current[index]?.select?.();
      } else {
        crInputRefs.current[index]?.focus();
        crInputRefs.current[index]?.select?.();
      }
    }
  };

  // Amount blur: trigger balance check & auto row generation
  const handleAmountBlur = (index, field) => {
    if (onAmountComplete) {
      onAmountComplete(index, field, false);
    }
  };

  // Keyboard navigation: Enter on amount input -> balance check, advance to next row or narration
  const handleAmountKeyDown = (e, index, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (onAmountComplete) {
        onAmountComplete(index, field, true);
      }
      setTimeout(() => {
        let curDr = 0;
        let curCr = 0;
        const drInputs = document.querySelectorAll(".vch_dr_input");
        const crInputs = document.querySelectorAll(".vch_cr_input");
        drInputs.forEach((el) => { curDr += parseFloat(el.value) || 0; });
        crInputs.forEach((el) => { curCr += parseFloat(el.value) || 0; });
        const isBalancedNow = curDr > 0 && Math.round(curDr * 100) === Math.round(curCr * 100);

        if (isBalancedNow && index >= 1) {
          const narrationEl = document.getElementById("vch_narration_input");
          if (narrationEl) {
            narrationEl.focus();
            return;
          }
        }

        const nextIndex = index + 1;
        if (ledgerSelectRefs.current[nextIndex]) {
          ledgerSelectRefs.current[nextIndex]?.focus?.();
        }
      }, 70);
    }
  };

  return (
    <div className="vch_table_responsive_wrapper">
      <table className="vch_entries_table">
        <thead>
          <tr>
            <th className="vch_col_sr">Sr. No.</th>
            <th className="vch_col_type">Dr / Cr</th>
            <th className="vch_col_ledger">
              Ledger Account <span className="vch_required_star">*</span>
            </th>
            <th className="vch_col_dr">Dr. (₹)</th>
            <th className="vch_col_cr">Cr. (₹)</th>
            <th className="vch_col_actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const hasLedger = Boolean(row.ledger);
            const isFilled = Boolean(row.ledger || row.dr || row.cr);
            const rowType = row.type || (parseFloat(row.cr) > 0 && !parseFloat(row.dr) ? "Cr" : "Dr");
            const isDr = rowType === "Dr";

            // Filter ledgers based on voucher type + Dr/Cr
            const filteredLedgers = isModifyMode
              ? allLedgers
              : getFilteredLedgers(allLedgers, voucherType, rowType);

            // Construct react-select options list
            const options = filteredLedgers.map((l) => {
              const lName = l.name || l.LedgerName;
              const lCode = l.headCode || l.HEAD_CODE || l.id;
              const lGroup = l.group || l.GroupName || l.GroupNAME || "";
              return {
                value: lName,
                label: lName,
                headCode: lCode,
                group: lGroup,
              };
            });

            // If current row's ledger is not in filtered options, inject it so it remains visible
            if (row.ledger && !options.some((opt) => opt.value === row.ledger)) {
              options.unshift({
                value: row.ledger,
                label: row.ledger,
                headCode: row.headCode,
                group: "",
              });
            }

            const selectedOption = options.find((opt) => opt.value === row.ledger) || null;
            const isWarn = !hasLedger && (row.dr || row.cr);

            return (
              <tr key={row.id || index} className={`vch_entry_row ${isFilled ? "vch_row_active" : ""}`}>
                {/* Sr No */}
                <td className="vch_cell_sr">{index + 1}</td>

                {/* Dr / Cr Switch Option */}
                <td className="vch_cell_type">
                  <div
                    className="vch_drcr_switch"
                    role="group"
                    aria-label={`Row ${index + 1} Debit/Credit Selector`}
                  >
                    <button
                      type="button"
                      className={`vch_drcr_btn vch_drcr_dr ${isDr ? "active" : ""}`}
                      onClick={() => handleTypeChange(index, "Dr")}
                      title="Set to Debit (Dr)"
                    >
                      Dr
                    </button>
                    <button
                      type="button"
                      className={`vch_drcr_btn vch_drcr_cr ${!isDr ? "active" : ""}`}
                      onClick={() => handleTypeChange(index, "Cr")}
                      title="Set to Credit (Cr)"
                    >
                      Cr
                    </button>
                  </div>
                </td>

                {/* Ledger Account Searchable Input */}
                <td className="vch_cell_ledger">
                  <Select
                    ref={(el) => (ledgerSelectRefs.current[index] = el)}
                    className="vch_searchable_select"
                    classNamePrefix="vch_select"
                    styles={getSelectStyles(isWarn)}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                    options={options}
                    value={selectedOption}
                    onChange={(selected) => handleLedgerSelectChange(index, selected, isDr)}
                    onKeyDown={(e) => handleLedgerKeyDown(e, index, isDr)}
                    filterOption={filterOption}
                    placeholder="-- Type to search ledger --"
                    isClearable={true}
                    isSearchable={true}
                    openMenuOnFocus={true}
                    formatOptionLabel={formatOptionLabel}
                    noOptionsMessage={() => "No matching ledger found"}
                  />
                </td>

                {/* Debit Amount */}
                <td className="vch_cell_amount">
                  <input
                    ref={(el) => (drInputRefs.current[index] = el)}
                    type="text"
                    inputMode="decimal"
                    className={`vch_amount_input vch_dr_input ${!isDr ? "vch_input_disabled" : ""}`}
                    placeholder={isDr ? "0.00" : "—"}
                    value={isDr ? row.dr : ""}
                    disabled={!isDr}
                    tabIndex={isDr ? 0 : -1}
                    onChange={(e) => handleAmountChange(index, "dr", e.target.value)}
                    onKeyDown={(e) => handleAmountKeyDown(e, index, "dr")}
                    onBlur={() => handleAmountBlur(index, "dr")}
                    onFocus={(e) => {
                      if (isDr && (e.target.value === "0.00" || e.target.value === "0")) {
                        handleAmountChange(index, "dr", "");
                      }
                    }}
                  />
                </td>

                {/* Credit Amount */}
                <td className="vch_cell_amount">
                  <input
                    ref={(el) => (crInputRefs.current[index] = el)}
                    type="text"
                    inputMode="decimal"
                    className={`vch_amount_input vch_cr_input ${isDr ? "vch_input_disabled" : ""}`}
                    placeholder={!isDr ? "0.00" : "—"}
                    value={!isDr ? row.cr : ""}
                    disabled={isDr}
                    tabIndex={!isDr ? 0 : -1}
                    onChange={(e) => handleAmountChange(index, "cr", e.target.value)}
                    onKeyDown={(e) => handleAmountKeyDown(e, index, "cr")}
                    onBlur={() => handleAmountBlur(index, "cr")}
                    onFocus={(e) => {
                      if (!isDr && (e.target.value === "0.00" || e.target.value === "0")) {
                        handleAmountChange(index, "cr", "");
                      }
                    }}
                  />
                </td>

                {/* Actions: Delete Button */}
                <td className="vch_cell_actions">
                  <button
                    type="button"
                    className="vch_delete_row_btn"
                    title="Delete Row"
                    onClick={() => onDeleteRow(index)}
                    disabled={rows.length <= 2}
                  >
                    <i className="fa-regular fa-trash-can"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
